/**
 * Account session persistence (Final Phase Stage B). Stores the JWT + account_id in
 * localStorage so the login survives reloads. Identity only — no Sui keys live here.
 */
/**
 * Login method that produced a session. `supabase` = Supabase Auth (email / OAuth), no wallet.
 * `crazygames` = Login with CrazyGames inside their frame; the account behind it is still a
 * Supabase user, keyed by account_id like any web2 account.
 */
export type LoginChain = 'sui' | 'evm' | 'solana' | 'supabase' | 'crazygames';

const LOGIN_CHAINS: readonly LoginChain[] = ['sui', 'evm', 'solana', 'supabase', 'crazygames'];

/** The web2 logins: their player key is the account_id. */
export const WEB2_LOGIN_CHAINS: readonly LoginChain[] = ['supabase', 'crazygames'];

export type AccountSession = {
  token: string;
  accountId: string;
  /** Player key: the account_id for a Supabase account, or the wallet proven at a parked wallet login. */
  address: string | null;
  /** Chain the login was performed on; absent in legacy rows written before this field existed. */
  chain?: LoginChain;
  email?: string | null;
  displayName?: string | null;
  authUserId?: string;
};

const KEY = 'suikamon.session';

/** Sui addresses are 32-byte hex (0x + 64 chars); EVM are 20-byte (0x + 40). */
const SUI_ADDRESS_PATTERN = /^0x[0-9a-fA-F]{64}$/;

export function getSession(): AccountSession | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<AccountSession>;
    if (typeof parsed?.token === 'string' && typeof parsed?.accountId === 'string') {
      // Tolerate legacy rows written before `address` / `chain` existed.
      const chain = LOGIN_CHAINS.includes(parsed.chain as LoginChain) ? (parsed.chain as LoginChain) : undefined;
      return {
        token: parsed.token,
        accountId: parsed.accountId,
        address: chain && WEB2_LOGIN_CHAINS.includes(chain) ? parsed.accountId : parsed.address ?? null,
        ...(chain ? { chain } : {}),
        ...(typeof parsed.email === 'string' ? { email: parsed.email } : {}),
        ...(typeof parsed.displayName === 'string' ? { displayName: parsed.displayName } : {}),
        ...(typeof parsed.authUserId === 'string' ? { authUserId: parsed.authUserId } : {}),
      };
    }
    return null;
  } catch {
    return null;
  }
}

export function setSession(session: AccountSession): void {
  localStorage.setItem(KEY, JSON.stringify(session));
}

export function clearSession(): void {
  localStorage.removeItem(KEY);
}

/**
 * Whether the player's latest login was via a Sui wallet — the only case where the app should
 * auto-reconnect the Sui (Slush) wallet on load. With no session we allow it (the legacy
 * "connect a Sui wallet to play" path / a returning Sui user). An EVM/Solana/email login means
 * the latest connected wallet is NOT Slush, so Sui auto-connect must stay disabled. Legacy rows
 * without a `chain` fall back to the address shape (Sui = 32-byte hex).
 */
export function latestLoginIsSui(session: AccountSession | null): boolean {
  if (!session) return true;
  if (session.chain) return session.chain === 'sui';
  return SUI_ADDRESS_PATTERN.test(session.address ?? '');
}
